/**
 * chat-live-state.ts — JAISIU-activity-always-visible
 *
 * Per-session live-run cache. The webchat host carries a single set of
 * "is this session mid-run?" fields (`chatRunId`, `chatStream`,
 * `chatReasoningStream`, `chatLiveRunHint`, etc.) which the chat view
 * derives from. When the operator switches tabs, those fields are
 * cleared (otherwise chat-A's stream bleeds into chat-B's view), so
 * switching back to a working chat-B leaves the bubble blank until
 * the next event lands.
 *
 * `ChatLiveStateSnapshot` mirrors the host's live fields to a per-key
 * Map so the next time the operator activates that session, we can
 * restore the bubble synchronously — the operator sees "Working…" the
 * instant they switch, not seconds later when the next delta arrives.
 *
 * JAISIU-3558: the snapshot's `stream` / `reasoningStream` fields are
 * now redundant for the transcript-rendering path (the live reply lives
 * inside `state.chatMessages` itself, as a streaming-tail entry keyed by
 * runId -- see `extendStreamingTail` in `ui/src/ui/controllers/chat.ts`).
 * The snapshot is still AUTHORITATIVE for the activity chrome (the
 * dock / tool-strip / subagent progress card / wait-ticker duration),
 * which derive from `liveRunHint`, `startedAt`, `mainRun`,
 * `activeFileToolPath`, and `compaction` and would otherwise have no
 * source of truth across a tab switch. Do NOT remove those fields.
 *
 * Pure data plumbing: the module knows about the cache shape and
 * keying, nothing about Lit / WS / agent events. The host wires it
 * into the existing event handlers and tab-switch path.
 *
 * Invariants:
 *   I1. `recordLiveState` is the single write path. Callers MUST go
 *       through it so the cache stays in lockstep with the host's
 *       active fields — never mirror the same fields in two places.
 *   I2. `consumeLiveState` removes the entry once restored. A snapshot
 *       is good for exactly one restore (otherwise stale state would
 *       leak across sessions).
 *   I3. `peekLiveState` is the non-destructive read used by the
 *       render-time derivation (`shouldShowLiveRunIndicator`,
 *       `buildAssistantTurns`, etc.). It never deletes.
 */

import type { CompactionStatus } from "../app-tool-stream.ts";
import type { ChatSubagentCard } from "../ui-types.ts";

export type ChatLiveStateSnapshot = {
  runId: string;
  startedAt: number;
  stream: string;
  reasoningStream: string;
  liveRunHint: string | null;
  activeFileToolPath: string | null;
  /** Snapshot of the main-run card at the time the live state was
   *  recorded. Used so the chat-render-time subagent strip knows the
   *  parent run's current phase (thinking / tool / etc.) without
   *  re-fetching from the registry. */
  mainRun: ChatSubagentCard | null;
  compaction: CompactionStatus | null;
  /** Timestamp at which an inject-path bubble flash was active. The
   *  chat view derives `shouldShowLiveRunIndicator` from this with a
   *  small TTL — see `INJECT_BUBBLE_FLASH_MS`. */
  injectedAt: number | null;
  /**
   * JAISIU-chat-switch-reload — collect-mode queue depth at the time
   * the snapshot was recorded. The webchat host carries a single
   * global `chatCollectQueueDepth` counter that the controller mutates
   * for every queued send against an in-flight run and clears on the
   * matching `chat.final`. When the operator switches tabs mid-queue
   * we want the destination tab to see the *outgoing* tab's depth so
   * the "Queued · N" pill stays accurate; restoring it from this
   * snapshot (consumed once per activation) keeps the depth pinned to
   * the session that owns it, not the global counter that belongs to
   * whichever tab happens to be active.
   */
  /**
   * JAISIU-types-sweep: optional on the type — the controller coalesces
   * missing values to `0` (no queued sends). Required at runtime
   * before the snapshot is exposed to renderers, because the SPA's
   * "Queued · N" pill needs an accurate integer. Optional here so
   * test fixtures and partially-built records don't have to thread
   * a value through.
   */
  collectQueueDepth?: number;
};

type Store = Map<string, ChatLiveStateSnapshot>;

const stores = new WeakMap<object, Store>();

function storeFor(host: object): Store {
  const existing = stores.get(host);
  if (existing) {
    return existing;
  }
  const fresh: Store = new Map();
  stores.set(host, fresh);
  return fresh;
}

/**
 * Persist a snapshot for `sessionKey`. The snapshot replaces any
 * existing entry for the key — concurrent snapshots for the same
 * session are coalesced because the chat controller's `chatRunId`
 * gate already enforces one-run-per-session.
 */
/** Mirror the host's current live-run fields into the per-session cache. */
export function recordLiveStateFromHost(
  host: object,
  sessionKey: string,
  fields: {
    chatRunId: string | null;
    chatStreamStartedAt: number | null;
    chatStream: string | null;
    chatReasoningStream: string | null;
    chatLiveRunHint?: string | null;
    chatActiveFileToolPath?: string | null;
    chatMainRun?: ChatLiveStateSnapshot["mainRun"];
    compactionStatus?: ChatLiveStateSnapshot["compaction"];
    chatInjectedAt?: number | null;
    chatCollectQueueDepth?: number;
  },
): void {
  const runId = fields.chatRunId?.trim();
  const startedAt = fields.chatStreamStartedAt;
  if (!runId || startedAt == null) {
    return;
  }
  recordLiveState(host, sessionKey, {
    runId,
    startedAt,
    stream: fields.chatStream ?? "",
    reasoningStream: fields.chatReasoningStream ?? "",
    liveRunHint: fields.chatLiveRunHint ?? null,
    activeFileToolPath: fields.chatActiveFileToolPath ?? null,
    mainRun: fields.chatMainRun ?? null,
    compaction: fields.compactionStatus ?? null,
    injectedAt: fields.chatInjectedAt ?? null,
    collectQueueDepth: fields.chatCollectQueueDepth ?? 0,
  });
}

export function recordLiveState(
  host: object,
  sessionKey: string,
  snapshot: ChatLiveStateSnapshot,
): void {
  const trimmed = sessionKey.trim();
  if (!trimmed) {
    return;
  }
  storeFor(host).set(trimmed, snapshot);
}

/**
 * Remove the snapshot for `sessionKey`. No-op when the key was never
 * recorded (e.g. the session never had an in-flight run).
 */
export function clearLiveState(host: object, sessionKey: string): void {
  const trimmed = sessionKey.trim();
  if (!trimmed) {
    return;
  }
  storeFor(host).delete(trimmed);
}

/**
 * Non-destructive read. Returns `undefined` when no snapshot exists
 * for the key. Use this from render-time derivations where you want
 * to know "is this session currently working?" without consuming the
 * entry.
 */
export function peekLiveState(host: object, sessionKey: string): ChatLiveStateSnapshot | undefined {
  const trimmed = sessionKey.trim();
  if (!trimmed) {
    return undefined;
  }
  return storeFor(host).get(trimmed);
}

/**
 * Read + remove the snapshot for `sessionKey`. Use this from the
 * tab-activation path so the snapshot lands on the host's live
 * fields exactly once. Returns `undefined` when no snapshot exists.
 */
export function consumeLiveState(
  host: object,
  sessionKey: string,
): ChatLiveStateSnapshot | undefined {
  const trimmed = sessionKey.trim();
  if (!trimmed) {
    return undefined;
  }
  const store = storeFor(host);
  const value = store.get(trimmed);
  if (value) {
    store.delete(trimmed);
  }
  return value;
}

/**
 * Iterate every persisted snapshot. Used by the test reset helper and
 * by future bulk-migration paths (e.g. dropping all sessions after a
 * hard reconnect).
 */
export function listLiveStates(host: object): IterableIterator<[string, ChatLiveStateSnapshot]> {
  return storeFor(host).entries();
}

/**
 * Test-only: clear every snapshot on `host`. Production code should
 * go through `clearLiveState` per-key.
 */
export function __resetLiveStatesForTests(host: object): void {
  storeFor(host).clear();
}
